import { formatMoney, type PublicLanguage } from '@app/shared'
import { useMutation, useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { orgHref } from '../lib/public-router'
import { returnedCheckout } from '../lib/returned-checkout'
import { readCivilDate, useToday } from '../lib/today'
import { useTRPC } from '../lib/trpc'
import { continueLabel } from '../lib/two-step-payment'
import {
  Button,
  Card,
  Figure,
  Notice,
  PaymentFlow,
  PaymentStep,
  PaymentThanks,
  ReturnedCheckoutNotice,
  useCheckoutConfirmation,
  useReturnedCheckout,
} from '../ui'
import { PublicNotFound } from '../ui/ErrorPage'
import { type PublicContact, PublicShell } from '../ui/PublicShell'
import { type GiftServerStatus, giftView } from './gift-view'
import './gift.css'

const money = formatMoney

/**
 * The page an office-sent payment link opens. No login: the `?token=` is the
 * whole authorization. It states the amount and what the gift is for, and
 * nothing about the billing account.
 *
 * Nothing here records the gift. Opening Pay mounts Stripe Checkout for this
 * link; a second open reuses the live checkout. The webhook is the only writer.
 * A paid or expired link is a page that says so, never an error, and a return
 * is verified by the server from its checkout id.
 */
export function GiftPage({
  orgId,
  onLogin,
}: {
  orgId: string
  onLogin?: () => void
}) {
  const trpc = useTRPC()
  const params = new URLSearchParams(window.location.search)
  const token = params.get('token') ?? ''
  const [completedCheckoutId, showCompletedCheckout] = useReturnedCheckout()
  /* Paid HERE, in this visit, rather than found paid: only the first is the
     payer's own act to be thanked for. A link opened after it was paid says
     so and names nothing charged. */
  const [paidHere, setPaidHere] = useState(false)
  const today = useToday()
  const confirmation = useCheckoutConfirmation({
    onComplete: (checkoutId) => {
      setPaidHere(true)
      showCompletedCheckout(checkoutId)
    },
  })

  const org = useQuery(trpc.donation.org.queryOptions({ shortId: orgId }))

  const link = useQuery({
    ...trpc.donation.officeLink.queryOptions({ shortId: orgId, token }),
    enabled: token.length > 0,
    retry: false,
    refetchOnWindowFocus: false,
  })
  const outcome = useQuery({
    ...trpc.donation.checkoutOutcome.queryOptions({
      shortId: orgId,
      checkoutId: completedCheckoutId ?? '',
    }),
    enabled: completedCheckoutId !== null,
  })
  const checkout = useMutation({
    ...trpc.donation.officeLinkCheckout.mutationOptions(),
  })

  if (org.isLoading || org.error || !org.data) {
    return (
      <Frame onLogin={onLogin}>
        {org.isLoading ? (
          <div className="gift">
            <Card className="gift__card">
              <Notice>Loading…</Notice>
            </Card>
          </div>
        ) : (
          <PublicNotFound />
        )}
      </Frame>
    )
  }

  const o = org.data
  const server: GiftServerStatus =
    !token || link.isError || !link.data || link.data.status === 'invalid'
      ? 'invalid'
      : link.data.status
  const returned = returnedCheckout({
    checkoutId: completedCheckoutId,
    status: outcome.data?.status,
    isError: outcome.isError,
  })
  const view = giftView(server, returned)
  const details = link.data && link.data.status !== 'invalid' ? link.data : null
  const isPayment = details?.kind === 'payment'
  const isArrangement = details?.kind === 'arrangement'
  const isTest = details?.test === true
  const figures = details ? (
    <Figure
      amount={money(details.amountCents)}
      label={`${isPayment ? 'Payment toward' : 'For'} ${details.what}.`}
    />
  ) : null

  return (
    <Frame
      name={o.name}
      nameHe={o.nameHe}
      language={o.publicLanguage}
      orgId={o.shortId}
      acceptsDonations={o.acceptsDonations}
      contact={o}
      onLogin={onLogin}
    >
      <div className="gift">
        <Card className="gift__card">
          <h1 className="gift__title">
            {isPayment
              ? 'Account payment'
              : isArrangement
                ? 'Membership payment'
                : 'This gift'}
          </h1>
          {token && link.isLoading ? (
            <p className="gift__muted">Loading…</p>
          ) : view === 'invalid' ? (
            <Notice>
              This link is not valid. Please use the link the office sent, or
              contact the office.
            </Notice>
          ) : view === 'expired' ? (
            <Notice>
              This link has expired. Please contact the office for a new one.
            </Notice>
          ) : view === 'paid' && paidHere && details ? (
            <PaymentThanks
              thanks={{
                headline: `Thank you for your ${isPayment ? 'payment' : 'gift'} of ${money(details.amountCents)}.`,
                receipt: 'There is nothing else for you to do.',
                record: [
                  {
                    label: isPayment ? 'Toward' : 'For',
                    value: details.what,
                  },
                  { label: 'Charged', value: money(details.amountCents) },
                  { label: 'Date', value: readCivilDate(today, 'long') },
                ],
              }}
            >
              <a href={orgHref(o.shortId, '')}>Back to {o.name}</a>
            </PaymentThanks>
          ) : view === 'paid' ? (
            <>
              <Notice tone="info">
                Thank you — this {isPayment ? 'payment' : 'gift'} has been paid.
                Nothing more will be charged.
              </Notice>
              {figures}
            </>
          ) : view === 'processing' ||
            view === 'checking' ||
            view === 'check_error' ? (
            <>
              <ReturnedCheckoutNotice state={returned} />
              {figures}
            </>
          ) : (
            <PaymentFlow>
              <ReturnedCheckoutNotice state={returned} />
              {figures}
              {isTest ? (
                <Notice tone="info">
                  This is a test of the page they will receive. Nothing can be
                  charged or saved from this link.
                </Notice>
              ) : null}
              {checkout.error && (
                <Notice tone="error">{checkout.error.message}</Notice>
              )}
              {checkout.data?.reason && !checkout.data.card ? (
                <Notice>{checkout.data.reason}</Notice>
              ) : null}
              {!isTest ? (
                <>
                  <Button
                    onClick={() => checkout.mutate({ shortId: orgId, token })}
                    disabled={checkout.isPending}
                  >
                    {continueLabel(checkout.isPending)}
                  </Button>
                  <p className="gift__fine">
                    Next, your card. Nothing is charged yet.
                  </p>
                </>
              ) : null}
              {checkout.data?.card && checkout.data.checkoutId && details && (
                <PaymentStep
                  orgId={orgId}
                  card={checkout.data.card}
                  what={details.what}
                  who={isPayment ? 'Account payment' : undefined}
                  amount={money(details.amountCents)}
                  payLabel={`Pay ${money(details.amountCents)}`}
                  confirmation={confirmation}
                  onBack={() => {
                    checkout.reset()
                    confirmation.reset()
                  }}
                />
              )}
            </PaymentFlow>
          )}
        </Card>
      </div>
    </Frame>
  )
}

function Frame({
  name = null,
  nameHe,
  language,
  orgId = null,
  acceptsDonations = false,
  contact,
  onLogin,
  children,
}: {
  name?: string | null
  nameHe?: string | null
  language?: PublicLanguage
  orgId?: string | null
  acceptsDonations?: boolean
  contact?: PublicContact | null
  onLogin?: () => void
  children: ReactNode
}) {
  return (
    <PublicShell
      name={name}
      nameHe={nameHe}
      language={language}
      orgId={orgId}
      acceptsDonations={acceptsDonations}
      contact={contact}
      onLogin={onLogin}
    >
      {children}
    </PublicShell>
  )
}
